import { parseISO } from "date-fns";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { DataCollectionSubmissionStatus } from "@entrilia/shared/api/dataCollectionTypes";
import { allDataCollectionSubmissionStatuses } from "@entrilia/shared/api/dataCollectionTypes";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateOnly } from "@entrilia/shared/utilities/dateUtils";
import { dataSubmissionStatusCaptionsMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../api/adminApi";
import type {
  DataCollectionRequestAuditory,
  DataCollectionSubmissionInfo,
  SearchSubmissionsRequest,
} from "../../../../api/types/dataCollectionTypes";
import { allDataCollectionRequestAuditoryValues } from "../../../../api/types/dataCollectionTypes";
import { dataCollectionRequestAuditoryCaptionsMap } from "../../../../utilities/enumCaptions";
import type { FilterState } from "../../../common/filters/filterState";
import type {
  DateFilter,
  MultiSelectFilter,
  SearchFilterDefinition,
  SelectOption,
  TableFilter,
  TableFilterDefinition,
} from "../../../common/filters/filterTypes";

const getObjectsAsLookupReferences = withErrorHandling(adminApi.getObjectsAsLookupReferences);
const getDataCollectionRequestTemplates = withErrorHandling(adminApi.getDataRequestTemplates);

const getRecipientOptions = async () => {
  const [refs, error] = await getObjectsAsLookupReferences("PortfolioCompany");
  if (error) {
    logError(error, "[getSelectOptions] getObjectsAsLookupReferences");
    return [];
  }

  return refs.map((r) => ({ value: r.objectId, label: r.primaryFieldValue }));
};

const getTemplates = async () => {
  const [templates, error] = await getDataCollectionRequestTemplates();
  if (error) {
    logError(error, "[getSelectOptions] getDataCollectionRequestTemplates");
    return [];
  }
  return templates.map((t) => ({ value: t.templateId, label: t.name })).sort(stringComparerBy((c) => c.label));
};

export const selectOptionsResolver = async (filterId: string): Promise<SelectOption[]> => {
  switch (filterId) {
    case "recipientObjectId":
      return getRecipientOptions();
    case "requestTemplateId":
      return getTemplates();
    default: {
      logError(`Unknown async select filterId: ${filterId}`, "[getSelectOptions]");
      return [];
    }
  }
};

export const allSubmissionsFilterDefinitions: TableFilterDefinition<DataCollectionSubmissionInfo>[] = [
  {
    id: "recipientObjectId",
    name: "Portfolio Company",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "requestTemplateId",
    name: "Template",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "status",
    name: "Status",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allDataCollectionSubmissionStatuses.map((status) => ({
      value: status,
      label: dataSubmissionStatusCaptionsMap[status],
    })),
  },
  {
    id: "auditory",
    name: "Recipient Type",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allDataCollectionRequestAuditoryValues.map((auditory) => ({
      value: auditory,
      label: dataCollectionRequestAuditoryCaptionsMap[auditory] ?? auditory,
    })),
  },
  {
    id: "reportingDate",
    name: "Reporting Date",
    type: "date",
    operatorOptions: ["range"],
  },
];

export const mySubmissionsSearchDefinition: SearchFilterDefinition<DataCollectionSubmissionInfo> = {
  getFieldValues: (row) => [row.recipientName, row.requestTemplateName],
};

export const mySubmissionsFilterDefinitions: TableFilterDefinition<DataCollectionSubmissionInfo>[] = [
  {
    id: "recipientObjectId",
    name: "Portfolio Company",
    type: "multi_select",
    operatorOptions: ["one_of"],
    getFieldValue: (row) => row.recipientName,
  },
  {
    id: "requestTemplateId",
    name: "Template",
    type: "multi_select",
    operatorOptions: ["one_of"],
    getFieldValue: (row) => row.requestTemplateName,
  },
  {
    id: "auditory",
    name: "Recipient Type",
    type: "multi_select",
    operatorOptions: ["one_of"],
    getFieldValue: (row) => row.auditory,
  },
  {
    id: "reportingDate",
    name: "Reporting Date",
    type: "date",
    operatorOptions: ["range"],
    getFieldValue: (row) => (row.reportingDate ? parseISO(row.reportingDate) : undefined),
  },
];

export const getDataSubmissionsFilterPayload = (
  filterState: FilterState<DataCollectionSubmissionInfo>
): Omit<SearchSubmissionsRequest, "range"> => {
  const getMultiSelectFilterValues = (
    filterId: string,
    visibleFilters: TableFilter<DataCollectionSubmissionInfo>[]
  ) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as
      MultiSelectFilter<DataCollectionSubmissionInfo> | undefined;
    const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
    return selectedValues?.length ? selectedValues : undefined;
  };

  const getDateRangeFilterValues = (filterId: string, visibleFilters: TableFilter<DataCollectionSubmissionInfo>[]) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as
      DateFilter<DataCollectionSubmissionInfo> | undefined;
    if (filter?.value?.operator !== "range") {
      return [undefined, undefined];
    }

    const { range } = filter.value;
    const from = range?.[0];
    const to = range?.[1];
    return [from ? convertISODateOnly(from) : undefined, to ? convertISODateOnly(to) : undefined];
  };

  const recipientObjectIds = getMultiSelectFilterValues("recipientObjectId", filterState.visibleFilters);

  const requestTemplateIds = getMultiSelectFilterValues("requestTemplateId", filterState.visibleFilters);

  const statuses = getMultiSelectFilterValues("status", filterState.visibleFilters) as
    DataCollectionSubmissionStatus[] | undefined;

  const auditory = getMultiSelectFilterValues("auditory", filterState.visibleFilters) as
    DataCollectionRequestAuditory[] | undefined;

  const [reportingDateFrom, reportingDateTo] = getDateRangeFilterValues("reportingDate", filterState.visibleFilters);

  return {
    searchTerm: filterState.search.value || undefined,
    recipientObjectIds,
    requestTemplateIds,
    statuses,
    auditory,
    reportingDateFrom,
    reportingDateTo,
  };
};
